import { inject, Injectable } from '@angular/core';
import {
  type UserNotificationActionSubmitKind,
  UserNotificationActionSubmitKind as ActionSubmitKind,
  UserNotificationIdempotencyScope,
  UserNotificationTargetRouteKey,
} from '@automatick/shared-ts';
import {
  NotificationsClientService,
  PresentationActionsClientService,
  type UserNotificationActionPayload,
  type UserNotificationResponse,
} from '@automatick/shared-ng';
import {
  defaultIfEmpty,
  finalize,
  map,
  of,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserNotificationCommandRegistry } from './user-notification-command-registry';
import { UserNotificationContractValidator } from './user-notification-contract-validator';

const DEFAULT_NOTIFICATION_ACTION_ID = 'default';
const CANONICAL_GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export interface UserNotificationActionOutcome {
  readonly workspaceId: string;
  readonly notificationId: string;
  readonly notification?: UserNotificationResponse;
  readonly message?: string;
}

@Injectable({ providedIn: 'root' })
export class UserNotificationActionExecutor {
  private readonly notificationsClient = inject(NotificationsClientService);
  private readonly presentationActionsClient = inject(
    PresentationActionsClientService,
  );
  private readonly registry = inject(UserNotificationCommandRegistry);
  private readonly contracts = new UserNotificationContractValidator();
  private readonly outcomesSubject =
    new Subject<UserNotificationActionOutcome>();

  readonly outcomes$ = this.outcomesSubject.asObservable();
  readonly leaseChanges$ = this.registry.changes$;

  activeNotificationIds(workspaceId: string): readonly string[] {
    return this.registry.activeActionNotificationIds(workspaceId);
  }

  submitAction(
    workspaceId: string,
    notification: UserNotificationResponse,
    actionKind: UserNotificationActionSubmitKind,
    actionId?: string | null,
  ): boolean {
    const notificationId = notification.notificationId?.trim();
    if (!notificationId || notification.workspaceId !== workspaceId) {
      return false;
    }
    const lease = this.registry.acquireAction(workspaceId, notificationId);
    if (!lease) {
      return false;
    }

    this.notificationsClient
      .submitUserNotificationAction({
        notificationId,
        body: {
          actionKind,
          actionId: actionId?.trim() || null,
          idempotencyKey: this.buildActionIdempotencyKey(
            notificationId,
            actionKind,
            actionId,
          ),
        },
      })
      .pipe(
        take(1),
        map((result): UserNotificationActionOutcome => {
          const ownedNotification =
            this.contracts.selectOwnedActionNotification(
              result,
              workspaceId,
              notificationId,
            );
          return ownedNotification
            ? { workspaceId, notificationId, notification: ownedNotification }
            : {
                workspaceId,
                notificationId,
                message:
                  'The action result could not be verified. Refresh notifications before trying again.',
              };
        }),
        defaultIfEmpty({
          workspaceId,
          notificationId,
          message:
            'The action result could not be verified. Refresh notifications before trying again.',
        }),
        catchError(() =>
          of({
            workspaceId,
            notificationId,
            message: 'Notification action could not be completed.',
          }),
        ),
        finalize(() => this.registry.releaseAction(lease)),
      )
      .subscribe((outcome) => this.outcomesSubject.next(outcome));
    return true;
  }

  submitResponseAction(
    workspaceId: string,
    notification: UserNotificationResponse,
    action: UserNotificationActionPayload,
  ): boolean {
    const notificationId = notification.notificationId?.trim();
    const presentationId = this.parseGuid(action.presentationId);
    const wakeHandleId = this.parseGuid(action.targetEntityId);
    const expectedStateVersion = this.normalizeExpectedStateVersion(
      action.expectedStateVersion,
    );
    if (
      !notificationId ||
      notification.workspaceId !== workspaceId ||
      action.targetRouteKey !==
        UserNotificationTargetRouteKey.PresentationAction ||
      !presentationId ||
      !wakeHandleId ||
      expectedStateVersion === null
    ) {
      this.outcomesSubject.next({
        workspaceId,
        notificationId: notificationId ?? '',
        message: 'Notification response could not be submitted.',
      });
      return false;
    }

    const lease = this.registry.acquireAction(workspaceId, notificationId);
    if (!lease) {
      return false;
    }
    const submittedActionId =
      action.intent?.trim() ||
      action.actionId?.trim() ||
      DEFAULT_NOTIFICATION_ACTION_ID;
    const failedResult: UserNotificationActionOutcome = {
      workspaceId,
      notificationId,
      message:
        'The response was not accepted. Refresh the notification before trying again.',
    };

    this.presentationActionsClient
      .submitPresentationAction({
        body: {
          presentationId,
          actionId: submittedActionId,
          wakeHandleId,
          expectedStateVersion,
          selectedChoiceIds: action.actionId?.trim()
            ? [action.actionId.trim()]
            : [],
          responseText:
            action.label?.trim() || action.actionId?.trim() || 'Continue',
          idempotencyKey: this.buildResponseActionIdempotencyKey(
            notificationId,
            action,
          ),
        },
      })
      .pipe(
        take(1),
        switchMap((result) => {
          if (
            !this.contracts.isSuccessfulPresentationAction(result, {
              presentationId,
              actionId: submittedActionId,
              wakeHandleId,
            })
          ) {
            return of(failedResult);
          }

          return this.notificationsClient
            .submitUserNotificationAction({
              notificationId,
              body: {
                actionKind: ActionSubmitKind.Complete,
                actionId: action.actionId?.trim() || null,
                idempotencyKey: this.buildActionIdempotencyKey(
                  notificationId,
                  ActionSubmitKind.Complete,
                  action.actionId,
                ),
              },
            })
            .pipe(
              take(1),
              map((completion): UserNotificationActionOutcome => {
                const ownedNotification =
                  this.contracts.selectOwnedActionNotification(
                    completion,
                    workspaceId,
                    notificationId,
                  );
                return ownedNotification
                  ? {
                      workspaceId,
                      notificationId,
                      notification: ownedNotification,
                    }
                  : {
                      workspaceId,
                      notificationId,
                      message:
                        'The action result could not be verified. Refresh notifications before trying again.',
                    };
              }),
              defaultIfEmpty(failedResult),
            );
        }),
        defaultIfEmpty(failedResult),
        catchError(() =>
          of({
            workspaceId,
            notificationId,
            message: 'Notification response could not be submitted.',
          }),
        ),
        finalize(() => this.registry.releaseAction(lease)),
      )
      .subscribe((outcome) => this.outcomesSubject.next(outcome));
    return true;
  }

  private buildActionIdempotencyKey(
    notificationId: string,
    actionKind: string,
    actionId?: string | null,
  ): string {
    return [
      UserNotificationIdempotencyScope.Action,
      notificationId,
      actionKind,
      actionId?.trim() || DEFAULT_NOTIFICATION_ACTION_ID,
    ].join(':');
  }

  private buildResponseActionIdempotencyKey(
    notificationId: string,
    action: UserNotificationActionPayload,
  ): string {
    return [
      UserNotificationIdempotencyScope.Action,
      notificationId,
      'response',
      action.targetEntityId?.trim() || 'target',
      action.actionId?.trim() || DEFAULT_NOTIFICATION_ACTION_ID,
    ].join(':');
  }

  private parseGuid(value: string | null | undefined): string | null {
    const normalized = value?.trim();
    return normalized &&
      normalized.toLowerCase() !== EMPTY_GUID &&
      CANONICAL_GUID_PATTERN.test(normalized)
      ? normalized
      : null;
  }

  private normalizeExpectedStateVersion(
    value: number | null | undefined,
  ): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
      ? value
      : null;
  }
}
